import { useCallback } from 'react';
import { SprintAPI } from '../utils/api';
import { getActiveWorkspaceId } from '../utils/api/workspaceContext';
import { markSprintCreatePending, clearSprintCreatePending } from '../utils/pendingSprintCreates';
import {
  markPendingSprintRename,
  peekPendingSprintRename,
} from '../utils/pendingSprintRenames';

function workspacePin() {
  return { workspaceId: getActiveWorkspaceId() };
}

/**
 * useSprintCrud Hook
 * 
 * Handles basic sprint CRUD operations:
 * - Add sprint (for nested sprint creation)
 * - Update sprint (sync local changes to global state)
 * - Rename sprint (from sidebar / header; syncs parent link mission when nested)
 * - Create sprint (new sprint creation)
 */
export function useSprintCrud({
  sprints,
  setSprints,
  saveSprint,
  saveSprintImmediate,
}) {
  // Add a sprint to the list (for nested sprint creation)
  const handleAddSprint = useCallback(async (newSprint) => {
    if (!newSprint?.id) return;

    const existing = (sprints || []).find((s) => s.id === newSprint.id);
    if (existing) {
      setSprints((prev) => prev.map((s) => (s.id === newSprint.id ? { ...s, ...newSprint } : s)));
      return;
    }

    setSprints((prev) => {
      if (prev.some((s) => s.id === newSprint.id)) {
        return prev.map((s) => (s.id === newSprint.id ? { ...s, ...newSprint } : s));
      }
      return [...prev, newSprint];
    });
    markSprintCreatePending(newSprint.id);
    try {
      await SprintAPI.create(newSprint, workspacePin());
      console.log('✅ Nested sprint saved to API');
    } catch (error) {
      console.error('Failed to save nested sprint:', error);
    } finally {
      clearSprintCreatePending(newSprint.id);
    }
  }, [sprints, setSprints]);

  // Update a sprint in the list (for syncing local changes to global state)
  // ALWAYS saves immediately - no batching!
  // Pass { immediate: true } to bypass the 2s save throttle (variables, etc.).
  const handleUpdateSprint = useCallback((updatedSprint, options = {}) => {
    // Update local state immediately
    setSprints(prev => prev.map(s => s.id === updatedSprint.id ? updatedSprint : s));
    
    // Save to API immediately
    if (options.immediate && saveSprintImmediate) {
      saveSprintImmediate(updatedSprint);
    } else {
      saveSprint(updatedSprint);
    }
  }, [setSprints, saveSprint, saveSprintImmediate]);

  // Rename a sprint (from sidebar context menu or campaign header).
  // IMPORTANT: Must fetch full sprint data before updating to avoid overwriting tasks!
  // If this is a nested campaign, also rename the parent link mission so the two stay locked.
  // (The reverse — renaming the mission — already syncs the campaign via useTaskEdit.)
  const handleRenameSprint = useCallback(async (sprintId, newName) => {
    // Supersedes an in-flight mission→campaign rename of the same record.
    markPendingSprintRename(sprintId, newName);
    // Update local state immediately (optimistic update)
    setSprints(prev => prev.map(s => 
      s.id === sprintId ? { ...s, name: newName } : s
    ));
    
    // Save to API - MUST fetch full data first!
    // The sidebar only has metadata (no tasks array), so we need to get the full sprint
    try {
      const fullSprint = await SprintAPI.get(sprintId, workspacePin());
      if (!fullSprint) {
        console.error('Failed to fetch full sprint for rename:', sprintId);
        return;
      }
      if (peekPendingSprintRename(sprintId) !== newName) return;

      await SprintAPI.update(sprintId, { ...fullSprint, name: newName }, workspacePin());
      console.log('✅ Sprint renamed:', newName);

      // Sync parent link mission name when this campaign is nested
      const parentSprintId = fullSprint.parentSprintId;
      if (!parentSprintId) return;

      try {
        const parentSprint = await SprintAPI.get(parentSprintId, workspacePin());
        if (!parentSprint?.tasks) return;

        let parentTaskId = fullSprint.parentTaskId;
        if (!parentTaskId) {
          // parentTaskId is not always stored on the campaign; resolve via nestedSprintId
          parentTaskId = parentSprint.tasks.find(t => t.nestedSprintId === sprintId)?.id;
        }
        if (!parentTaskId) return;

        const linkTask = parentSprint.tasks.find(t => t.id === parentTaskId);
        if (!linkTask || linkTask.name === newName) return;

        const updatedParent = {
          ...parentSprint,
          tasks: parentSprint.tasks.map(t =>
            t.id === parentTaskId
              ? { ...t, name: newName, updatedAt: new Date().toISOString() }
              : t
          ),
        };

        setSprints(prev => prev.map(s =>
          s.id === parentSprintId ? updatedParent : s
        ));

        // Let an open parent SprintDetail update its local task name without
        // going through handleRenameTask (which would sync back to this campaign).
        window.dispatchEvent(new CustomEvent('taskNameChanged', {
          detail: {
            parentSprintId,
            taskId: parentTaskId,
            newName,
          },
        }));

        await SprintAPI.update(parentSprintId, updatedParent, workspacePin());
        console.log('✅ Parent mission name synced with nested campaign');
      } catch (error) {
        console.error('Failed to sync parent mission name:', error);
      }
    } catch (error) {
      console.error('Failed to rename sprint:', error);
    }
  }, [setSprints]);

  // Create a new sprint
  const handleSprintCreated = useCallback(async (newSprint, onSuccess) => {
    console.log('Sprint created:', newSprint);
    try {
      // Save to API
      const savedSprint = await SprintAPI.create(newSprint, workspacePin());
      setSprints(prev => [...prev, savedSprint]);
      onSuccess?.(savedSprint);
      return savedSprint;
    } catch (error) {
      console.error('Failed to create sprint:', error);
      // Still add locally for now
      setSprints(prev => [...prev, newSprint]);
      onSuccess?.(newSprint);
      return newSprint;
    }
  }, [setSprints]);

  return {
    handleAddSprint,
    handleUpdateSprint,
    handleRenameSprint,
    handleSprintCreated,
  };
}

export default useSprintCrud;

